import React, { useEffect, useState } from 'react'
import { gql, useQuery } from 'urql'
import { DateTime } from 'luxon'
import { Box, Button, Collapse, Typography, useTheme } from '@mui/material'
import {
  Add,
  ArrowUpward,
  CheckCircleOutline,
  ExpandMore,
  Notifications,
  Remove,
  Settings,
  Visibility,
} from '@mui/icons-material'
import { MessageHistory } from '../alerts/AlertDetailLogs'
import { useBrand } from './ui'

// GoAlert's urql client suspends by default; render our own loading state.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsTimeline($id: Int!, $input: AlertRecentEventsOptions) {
    alert(id: $id) {
      id
      recentEvents(input: $input) {
        nodes {
          id
          timestamp
          message
          state {
            details
            status
          }
          messageID
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

type Entry = {
  id: number
  timestamp: string
  message: string
  state: { details: string; status: 'OK' | 'WARN' | 'ERROR' | null } | null
  messageID: string | null
}
type Group = { first: Entry; count: number }

type Kind =
  | 'created'
  | 'notified'
  | 'escalated'
  | 'acked'
  | 'closed'
  | 'policy'
  | 'request'
  | 'other'

function kindOf(m: string): Kind {
  const s = m.toLowerCase()
  if (s.startsWith('created')) return 'created'
  if (s.startsWith('notification sent') || s.includes('notified'))
    return 'notified'
  if (s.startsWith('escalation requested')) return 'request'
  if (s.startsWith('escalated')) return 'escalated'
  if (s.startsWith('acknowledged')) return 'acked'
  if (s.startsWith('closed')) return 'closed'
  if (s.startsWith('policy updated')) return 'policy'
  return 'other'
}

const icons: Record<Kind, React.ReactNode> = {
  created: <Add fontSize='small' />,
  notified: <Notifications fontSize='small' />,
  escalated: <ArrowUpward fontSize='small' />,
  request: <ArrowUpward fontSize='small' />,
  acked: <Visibility fontSize='small' />,
  closed: <CheckCircleOutline fontSize='small' />,
  policy: <Settings fontSize='small' />,
  other: <Remove fontSize='small' />,
}

// friendly shortens Power Automate webhook hosts to what they are.
function friendly(m: string): string {
  return m.replace(
    /\S+\.(powerplatform\.com|logic\.azure\.com) \(Webhook\)/g,
    'Teams channel (webhook)',
  )
}

function dayLabel(d: DateTime): string {
  const today = DateTime.local().startOf('day')
  if (d >= today) return 'Today'
  if (d >= today.minus({ days: 1 })) return 'Yesterday'
  return d.toFormat('cccc d LLLL')
}

function Event(props: { g: Group; last: boolean }): React.JSX.Element {
  const theme = useTheme()
  const c = useBrand()
  const [open, setOpen] = useState(false)
  const e = props.g.first
  const kind = kindOf(e.message)
  const st = e.state?.status
  const tone =
    st === 'ERROR'
      ? theme.palette.error.main
      : st === 'OK'
        ? c.ok
        : st === 'WARN'
          ? c.warn
          : kind === 'acked' || kind === 'closed'
            ? c.ok
            : c.muted
  const t = DateTime.fromISO(e.timestamp)
  const expandable = Boolean(e.messageID)

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {/* rail */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 30,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: tone,
            border: `1.5px solid ${tone}`,
            bgcolor: 'background.paper',
          }}
        >
          {icons[kind]}
        </Box>
        {!props.last && (
          <Box
            sx={{ flex: 1, width: 2, bgcolor: c.line, my: 0.25, minHeight: 10 }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pb: props.last ? 0 : 1.5 }}>
        <Box
          onClick={() => expandable && setOpen(!open)}
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            cursor: expandable ? 'pointer' : 'default',
            borderRadius: '8px',
            px: 1,
            py: 0.5,
            mx: -1,
            '&:hover': expandable ? { bgcolor: c.hover } : undefined,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, color: c.ink }}>
              {friendly(e.message)}
              {props.g.count > 1 && (
                <Box
                  component='span'
                  sx={{ ml: 1, fontSize: 12, color: c.muted }}
                >
                  ×{props.g.count}
                </Box>
              )}
            </Typography>
            {e.state?.details && (
              <Typography sx={{ fontSize: 12.5, color: tone }}>
                {st === 'ERROR' && !/^failed/i.test(e.state.details)
                  ? 'Failed: '
                  : ''}
                {e.state.details}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{ fontSize: 12, color: c.muted, flexShrink: 0, pt: 0.25 }}
            title={t.toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
          >
            {t.toFormat('HH:mm')}
          </Typography>
          {expandable && (
            <ExpandMore
              fontSize='small'
              sx={{
                color: c.muted,
                transform: open ? 'rotate(180deg)' : undefined,
                transition: 'transform .15s',
              }}
            />
          )}
        </Box>
        {expandable && (
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                mt: 0.5,
                borderRadius: '10px',
                border: `1px solid ${c.line}`,
                bgcolor: c.bg,
                px: 1,
              }}
            >
              <MessageHistory messageID={e.messageID as string} noIndent />
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  )
}

// Timeline shows an alert's log as a vertical timeline, newest first:
// icons per event kind, delivery state, repeats folded, day separators.
export default function Timeline(props: {
  alertID: number
}): React.JSX.Element {
  const c = useBrand()
  const [limit, setLimit] = useState(50)
  const [q, refetch] = useQuery({
    query,
    variables: { id: props.alertID, input: { limit } },
    context: noSuspense,
    requestPolicy: 'cache-and-network',
  })
  useEffect(() => {
    const t = setInterval(
      () => refetch({ requestPolicy: 'network-only' }),
      15_000,
    )
    return () => clearInterval(t)
  }, [refetch])

  const entries: Entry[] = [...(q.data?.alert?.recentEvents?.nodes ?? [])].sort(
    (a: Entry, b: Entry) => b.timestamp.localeCompare(a.timestamp),
  )
  if (q.error)
    return (
      <Typography sx={{ color: 'error.main' }}>{q.error.message}</Typography>
    )
  if (!q.data) return <Typography sx={{ color: c.muted }}>Loading…</Typography>
  if (!entries.length)
    return (
      <Typography sx={{ color: c.muted }}>Nothing happened yet.</Typography>
    )

  // split into days, fold repeats within each day
  const days: { day: string; groups: Group[] }[] = []
  for (const e of entries) {
    const d = dayLabel(DateTime.fromISO(e.timestamp))
    if (!days.length || days[days.length - 1].day !== d)
      days.push({ day: d, groups: [] })
    const cur = days[days.length - 1]
    const last = cur.groups[cur.groups.length - 1]
    // fold repeats (e.g. five "Policy updated by …" in a row)
    if (
      last &&
      !e.messageID &&
      !last.first.messageID &&
      last.first.message === e.message
    )
      last.count++
    else cur.groups.push({ first: e, count: 1 })
  }

  return (
    <Box>
      {days.map((d, i) => (
        <Box key={d.day} sx={{ mt: i ? 2 : 0 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: c.muted,
              mb: 1,
            }}
          >
            {d.day}
          </Typography>
          {d.groups.map((g, k) => (
            <Event key={g.first.id} g={g} last={k === d.groups.length - 1} />
          ))}
        </Box>
      ))}
      {q.data.alert.recentEvents.pageInfo.hasNextPage && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          <Button
            size='small'
            disabled={q.fetching}
            onClick={() => setLimit(limit + 100)}
          >
            Show older events
          </Button>
        </Box>
      )}
    </Box>
  )
}
